import type { AllowedState } from '../content/states.types';
import type { IconType } from '../icons/icons.types';
/** Breakpoints for responsive component behavior. */
export type TableBreakpoint = 'mini' | 'small' | 'nav' | 'medium' | 'large' | 'xlarge';

/** What UserBadge needs of a user (UserBody, SessionBody and the like fit). */
export interface UserBadgeData {
    user_id?: number | string | null;
    username: string;
    acronym: string | null;
    color: string | null;
    nickname?: string | null;
    given_name?: string | null;
    family_name?: string | null;
    titles?: string | null;
}

/** `username` = the user name, `name` = nickname, else given name, else user name (UserService.getDisplayName()). */
/** `acronym` = the badge alone, the user name as tooltip. */
export const allowedUserShows = ['username', 'name', 'acronym'] as const;
export type AllowedUserShow = (typeof allowedUserShows)[number];

/** Allowed fields to display inside the UserBadge tooltip. */
export const allowedUserTooltipFields = ['username', 'person', 'id', 'user_id', 'acronym', 'color', 'name'] as const;
export type UserTooltipField = (typeof allowedUserTooltipFields)[number];

/** What TeamBadge needs of a team (TeamBody fits). */
export interface TeamBadgeData {
    team_id?: number | string | null;
    team: string;
    acronym: string;
    color: string | null;
    /** A system team (USER, ADMIN, ROOT) — only in the tooltip. */
    is_protected?: boolean;
}

/** `team` = acronym and name, `acronym` = the badge alone, the name as tooltip. */
export const allowedTeamShows = ['team', 'acronym'] as const;
export type AllowedTeamShow = (typeof allowedTeamShows)[number];

/** What PersonBadge needs of a person (PersonBody fits). */
export interface PersonBadgeData {
    titles?: string | null;
    /** Shown in italics below the name when PersonBadge gets `withNickname`. */
    nickname?: string | null;
    given_name?: string | null;
    family_name?: string | null;
}

export type PersonNameData = PersonBadgeData;

/** An icon of an `IconsBadge`: the state gives its colour, the badge has none. */
export interface BadgeIcon {
    icon: IconType;
    /** Its colour; without, the colour of the text. */
    state?: AllowedState;
}

/** What a hit of the big search shows: the richest form given, else `text`. */
export interface SearchHitData {
    person?: PersonBadgeData;
    user?: UserBadgeData;
    team?: TeamBadgeData;
    tag?: string;
    text?: string;
}

/** What the big search tells about a hit beside it: whatever its module has, then where it was found. */
export interface SearchInfoData {
    email?: string | null;
    teams?: TeamBadgeData[];
    fields: string[];
    /** The dev mark of a hit below the minimum quality. */
    isBelow?: boolean;
}
